import React, { useState } from "react";
import { toast } from '../../../../components/common/Toast/toast';
import Modal from "../../components/Modal.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { createTrainerLeave, deleteTrainerLeave } from "../../../../../services/TrainerLeaveService.js";
import { minutesToTimeLabel } from "../../classroomSchedule.js";
import TimeSummary from "./TimeSummary.jsx";

const REASONS = [
  "Sick leave",
  "Personal / family emergency",
  "Sanctioned leave",
  "Training / meeting",
  "Absent without notice",
  "Other",
];

const trainerName = (t) => t?.trainerName || t?.name || t?.trainerEmail || "Unassigned";

// A session row that names its batch is the bridge: click, Enter or Space.
const batchRowProps = (session, onOpenBatch) =>
  onOpenBatch && session.batchId
    ? {
        className: "pgm-row--open",
        tabIndex: 0,
        role: "button",
        "aria-label": `Open batch ${session.batchName || ""}`.trim(),
        onClick: () => onOpenBatch(session.batchId),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenBatch(session.batchId);
          }
        },
      }
    : {};

/* `onSchedule()`, when given, puts Schedule in the footer of a free day, so
   the trainer can be booked straight from here. `onOpenBatch(batchId)`,
   when given, makes each session row the bridge to its batch. The page
   closes this popup before either hand-off.
   `onEditBooking(booking)`, when given, puts an Edit on each session row:
   the booking form opens over this popup, which stays up behind it. */
const TrainerDetailModal = ({ row, canEdit, todayStr, scheduleLabel, rangeNote, onClose, onChanged, onSchedule, onOpenBatch, onEditBooking }) => {
  const { trainer, status, sessions, leave, freeWindows, summary } = row;
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ fromDate: todayStr, toDate: todayStr, reason: REASONS[0], note: "" });

  const submitLeave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createTrainerLeave({
        trainerId: trainer.id,
        fromDate: new Date(form.fromDate).toISOString(),
        toDate: new Date(form.toDate).toISOString(),
        reason: form.reason,
        note: form.note,
      });
      // The done panel replaces the body and the popup closes itself after
      // five seconds (Modal); the page reloads behind it.
      toast.success("Trainer marked on leave");
      onChanged();
    } catch (error) {
      toast.error(error?.response?.data?.message?.message || "Failed to mark leave");
    } finally {
      setSaving(false);
    }
  };

  const endLeave = async () => {
    if (!leave) return;
    setSaving(true);
    try {
      await deleteTrainerLeave(leave.id);
      toast.success("Leave ended");
      onChanged();
    } catch (error) {
      toast.error("Failed to end leave");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      eyebrow="Trainer"
      title={trainerName(trainer)}
      sub={trainer.trainerEmail || trainer.specialization || undefined}
      onClose={onClose}
      size="wide"
      labelledBy="cls-trainer-detail-title"
      footer={
        <>
          <div className="pgm-actions">
            {/* On ANY day this trainer is available, not only a wholly
                empty one. A day with one class still has hours left in
                it, and `status === "free"` hid Schedule the moment a
                single session existed -- the box above would say twelve
                hours free with no way to book any of them.

                Absent is the one exception, and it is not about being
                busy: the trainer is on leave, so there is nothing to
                book. */}
            {onSchedule && status !== "absent" && (
              <button type="button" className="pgm-btn" onClick={() => onSchedule()}>
                Schedule
              </button>
            )}
            {canEdit && status === "absent" && (
              <button
                type="button"
                className="pgm-btn"
                disabled={saving}
                onClick={endLeave}
              >
                {saving ? "Saving..." : "End leave"}
              </button>
            )}
            {canEdit && status !== "absent" && !showLeaveForm && (
              <button
                type="button"
                className="pgm-btn pgm-btn--danger"
                onClick={() => setShowLeaveForm(true)}
              >
                Mark on leave
              </button>
            )}
          </div>
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <dl className="quiz-facts">
        <div>
          <dt>Status</dt>
          <dd>
            {status === "teaching" && <StatusChip status="teaching">Teaching</StatusChip>}
            {status === "free" && <StatusChip status="free">Free</StatusChip>}
            {status === "absent" && <StatusChip status="absent">Absent</StatusChip>}
          </dd>
        </div>
        <div>
          <dt>Specialization</dt>
          <dd>{trainer.specialization || "\u2014"}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{trainer.trainerEmail || "\u2014"}</dd>
        </div>
      </dl>

      {status === "absent" && leave && (
        <p className="att-note att-note--warn">
          {leave.reason}
          {leave.note ? ` \u2014 ${leave.note}` : ""}
        </p>
      )}

      <section className="pgm-section">
        <h5 className="pgm-section-title">{scheduleLabel || "Today's schedule"}</h5>

        {sessions.length ? (
          <div className="pgm-tablescroll">
            <table className="pgm-table">
              <thead>
                <tr>
                  <th className="pgm-num">#</th>
                  <th>Busy</th>
                  <th>Batch</th>
                  <th>Room</th>
                  {onEditBooking && <th className="pgm-num">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sessions.map((session, i) => (
                  <tr key={session.batchId} {...batchRowProps(session, onOpenBatch)}>
                    <td className="pgm-num">{i + 1}</td>
                    <td>
                      {minutesToTimeLabel(session.startMin)} &ndash; {minutesToTimeLabel(session.endMin)}
                    </td>
                    <td>{session.batchName}</td>
                    <td>{session.classroomNo || "\u2014"}</td>
                    {/* The whole cell is kept off the row's open-the-batch
                        click -- that hand-off closes this popup, and a
                        click just beside Edit must not take it down. */}
                    {onEditBooking && (
                      <td
                        className="pgm-num"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {session.booking && (
                          <button
                            type="button"
                            className="pg-btn pg-btn--main"
                            onClick={() => onEditBooking(session.booking)}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pgm-empty">No sessions scheduled that day.</p>
        )}

        {rangeNote && <p className="att-note">{rangeNote}</p>}
      </section>

      {/* When the trainer is free that day, to the minute -- the same list
          the lab popup shows. The API sends it on every day cell
          (classroommanagement.service.js computeFreeWindows); it was on
          this row all along and simply never drawn. An absent trainer's
          day has none. */}
      {/* The day as hours. This was a row of "free" pills with no totals
          and no sense of proportion -- you could read the gaps but not
          how much of the day they came to. An absent trainer's day has
          neither: there is nothing to be busy or free within. */}
      {summary && status !== "absent" && (
        <TimeSummary
          summary={summary}
          sessions={sessions}
          freeWindows={freeWindows}
          dateLabel={scheduleLabel}
        />
      )}

      {canEdit && showLeaveForm && (
        <section className="pgm-section">
          <h5 className="pgm-section-title">Mark this trainer absent</h5>

          <form className="att-form" noValidate onSubmit={submitLeave}>
            <div className="mcq-options">
              <div className="pg-field">
                <label className="pg-label" htmlFor="cls-td-from">From</label>
                <input
                  id="cls-td-from"
                  type="date"
                  className="pg-input"
                  value={form.fromDate}
                  onChange={(e) => setForm((f) => ({ ...f, fromDate: e.target.value }))}
                />
              </div>
              <div className="pg-field">
                <label className="pg-label" htmlFor="cls-td-to">To</label>
                <input
                  id="cls-td-to"
                  type="date"
                  className="pg-input"
                  value={form.toDate}
                  onChange={(e) => setForm((f) => ({ ...f, toDate: e.target.value }))}
                />
              </div>
              <div className="pg-field">
                <label className="pg-label" htmlFor="cls-td-reason">Reason</label>
                <select
                  id="cls-td-reason"
                  className="pg-select"
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="pg-field">
                <label className="pg-label" htmlFor="cls-td-note">Note</label>
                <input
                  id="cls-td-note"
                  className="pg-input"
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="pg-filters-actions">
              <button
                type="button"
                className="pg-btn"
                onClick={() => setShowLeaveForm(false)}
              >
                Cancel
              </button>
              <button type="submit" className="pg-btn pg-btn--main" disabled={saving}>
                {saving ? "Saving..." : "Mark absent"}
              </button>
            </div>
          </form>
        </section>
      )}
    </Modal>
  );
};

export default TrainerDetailModal;
